import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tooltip, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { Notice, PrimaryButton, SecondaryButton } from "../organisation/OrgModal";
import PlanChangeModal from "../subscription/PlanChangeModal";
import AddonsSection from "../subscription/AddonsSection";
import SuperAdminPlan from "./SuperAdminPlan";
import { homeOrganisation, readSession } from "../../utils/organisationApi";
import BillingHistory from "../subscription/BillingHistory";
import CurrentPlanCard from "../subscription/CurrentPlanCard";
import usePublicPlans from "../../hooks/usePublicPlans";
import useResetOnReturn from "../../hooks/useResetOnReturn";
import {
  apiErrorCode,
  apiErrorMessage,
  isUpgradeFrom,
  openBillingPortal,
  planChanged,
  takeCheckoutBaseline,
  planOf,
  PLAN_STATE,
  planEnded,
  planState,
  refreshPlan,
  usePlan,
  waitForPlanChange,
} from "../../utils/subscriptionData";

// Subscription & Billing: current plan, add-ons, history. Usage has its own tab. Owners and Admins only.
const OwnPlanSettings = () => {
  const theme = useTheme();
  const { data, status } = usePlan();
  const { plans, loading: plansLoading } = usePublicPlans();
  const [searchParams, setSearchParams] = useSearchParams();

  // The picker: { mode: "buy" | "upgrade" | "change" | "compare", planId? } or null
  const [picker, setPicker] = useState(null);
  const [notice, setNotice] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState("");
  // Back from the Stripe portal: the button must not stay on "Opening..."
  useResetOnReturn(() => setPortalBusy(false));

  const plan = data?.plan ?? null;
  const state = planState(plan);
  const row = planOf(plan);
  // A plan past its end date is over even before the server flags it, so it can neither be managed nor upgraded
  const ended = planEnded(plan);
  const hasPlan = state !== PLAN_STATE.NONE && state !== PLAN_STATE.LEGACY && !ended;
  const paid = hasPlan && state !== PLAN_STATE.TRIAL;
  const currentPublic = paid ? plans.find((item) => item.plan_id === row?.plan_id) || null : null;

  useEffect(() => {
    refreshPlan().catch(() => {});
  }, []);

  // Back from Stripe: wait for the webhook to switch the plan. A redirect alone proves nothing.
  const returned = useRef(false);
  useEffect(() => {
    const result = searchParams.get("checkout");
    if (!result || returned.current) return;
    returned.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    next.delete("session_id");
    setSearchParams(next, { replace: true });
    if (result === "cancelled") {
      setNotice("Payment cancelled. Nothing was charged.");
      return;
    }
    if (result !== "success") return;
    const baseline = takeCheckoutBaseline();
    if (!baseline) {
      // Nothing to compare with (new tab or cleared storage): show what the server says now
      refreshPlan().catch(() => {});
      setNotice("Thanks. Your current plan is shown below; it can take a moment to update after payment.");
      return;
    }
    setSyncing(true);
    waitForPlanChange((now) => planChanged(baseline, now)).then((done) => {
      setSyncing(false);
      setNotice(done ? "Payment received. Your plan is updated." : "Payment received. Your plan will update in a moment.");
    });
  }, [searchParams, setSearchParams]);

  // "Upgrade Plan" in the banner lands here with the picker already open
  useEffect(() => {
    if (searchParams.get("upgrade") !== "1" || status === "loading" || !data) return;
    setPicker({ mode: paid ? "upgrade" : "buy" });
    const next = new URLSearchParams(searchParams);
    next.delete("upgrade");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, status, data, paid]);

  const handleManage = async () => {
    setPortalBusy(true);
    setPortalError("");
    try {
      const url = await openBillingPortal();
      if (!url) throw new Error("No portal address returned");
      window.location.assign(url);
    } catch (err) {
      setPortalError(
        apiErrorCode(err) === "NO_BILLING_ACCOUNT"
          ? "There's no billing account yet. Choose a plan first."
          : apiErrorMessage(err, "Could not open the billing portal. Try again.")
      );
      setPortalBusy(false);
    }
  };

  const canUpgrade = paid && plans.some((item) => isUpgradeFrom(currentPublic, item));
  const stripeBilled = paid && plan?.source === "stripe";
  const manageNote = ended
    ? "Your plan has ended, so there is nothing to manage. Buy a plan to continue."
    : !paid
    ? "Manage Plan opens Stripe billing. It's available once you have a paid plan."
    : !stripeBilled
      ? "Billing for this plan is handled by our team. Contact support to change it."
      : "";

  const manageHint = stripeBilled ? "Opens Stripe: update your card, see invoices or cancel." : manageNote;

  if (status === "error" && !data) {
    return (
      <div className="p-6 max-md:p-4">
        <div role="alert" className="p-4 border border-red-300 bg-red-50 text-red-900 rounded flex flex-wrap items-center justify-between gap-3">
          <span>We couldn't load your subscription. Nothing has changed on your account.</span>
          <SecondaryButton onClick={() => refreshPlan().catch(() => {})}>Try again</SecondaryButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full overflow-auto" style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}>
      <Notice onDismiss={() => setNotice("")}>{notice}</Notice>
      {syncing && (
        <div role="status" className="mx-4 mt-3 p-2.5 rounded border border-blue-200 bg-blue-50 text-blue-900 text-sm">
          Payment received. Waiting for Stripe to confirm your plan...
        </div>
      )}
      <div className="w-full max-w-6xl flex flex-col gap-3 p-4">
        {/* 1. Current subscription */}
        <CurrentPlanCard plan={plan} addons={data?.addons} plans={plans} loading={!data} error={portalError}>
          {!hasPlan && <PrimaryButton onClick={() => setPicker({ mode: "buy" })}>Buy Plan</PrimaryButton>}
          {state === PLAN_STATE.TRIAL && !ended && <PrimaryButton onClick={() => setPicker({ mode: "buy" })}>Upgrade Plan</PrimaryButton>}
          {canUpgrade && <PrimaryButton onClick={() => setPicker({ mode: "upgrade" })}>Upgrade Plan</PrimaryButton>}
          <Tooltip title={manageHint} arrow>
            <span>
              <SecondaryButton onClick={handleManage} disabled={!stripeBilled || portalBusy} aria-describedby="manage-plan-note">
                {portalBusy ? "Opening..." : "Manage Plan"}
              </SecondaryButton>
            </span>
          </Tooltip>
          <span id="manage-plan-note" className="sr-only">
            {manageHint}
          </span>
        </CurrentPlanCard>

        {/* 2. Add-ons */}
        <AddonsSection data={data} plans={plans} plansLoading={plansLoading} state={state} onNotice={setNotice} onUpgrade={() => setPicker({ mode: "buy" })} />

        {/* 3. History */}
        <BillingHistory />
      </div>

      <PlanChangeModal
        isOpen={Boolean(picker)}
        mode={picker?.mode}
        initialPlanId={picker?.planId}
        plan={plan}
        onClose={() => setPicker(null)}
      />
    </div>
  );
};

// A Super Admin picks whose plan to look at (their own by default); everyone else sees their own organisation's.
const PlanSettings = () => {
  const session = useMemo(() => readSession(), []);
  if (!session.isSuperAdmin) return <OwnPlanSettings />;
  return <SuperAdminPlan home={homeOrganisation(session)} tokenOrgId={session.organisationId} own={<OwnPlanSettings />} />;
};

export default PlanSettings;
